import { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Trash2, KeyRound, ShieldCheck, ShieldHalf, X, Loader2, Search, Users, Info, Pencil, Building2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PasswordInput from "@/components/PasswordInput";
import FeatureToggles, { DEFAULT_FEATURES, normalizeFeatures } from "@/components/FeatureToggles";
import { toast } from "sonner";

const tabBtn = (on) => `flex-1 h-9 rounded-md text-sm font-semibold transition ${on ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`;

const ROLE_META = {
  super_admin: { label: "Super Admin", cls: "text-violet-700 bg-violet-50 border-violet-200", icon: ShieldCheck },
  admin: { label: "Admin", cls: "text-sky-700 bg-sky-50 border-sky-200", icon: ShieldHalf },
  staff: { label: "Staff", cls: "text-slate-600 bg-slate-100 border-slate-200", icon: Users },
};

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.staff;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${m.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {m.label}
    </span>
  );
}

const PERM_DEFS = [
  ["employees", "Employees"],
  ["sites", "Sites"],
  ["attendance", "Attendance"],
  ["reviews", "Reviews"],
  ["reports", "Reports"],
  ["businesses", "Businesses"],
];
const PRESETS = [
  ["HR Manager", ["employees", "sites", "reports"]],
  ["Standard Admin", ["sites", "attendance", "reviews"]],
  ["Full Access", ["employees", "sites", "attendance", "reviews", "reports", "businesses"]],
];

function PermissionPicker({ value, onChange, onPreset }) {
  const set = new Set(value || []);
  const toggle = (k) => {
    const n = new Set(set);
    n.has(k) ? n.delete(k) : n.add(k);
    onChange(PERM_DEFS.map(([x]) => x).filter((x) => n.has(x)));
  };
  return (
    <div data-testid="permission-picker">
      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Module Permissions</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {PRESETS.map(([name, perms]) => (
          <button key={name} type="button" data-testid={`preset-${name.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => onPreset(name, perms)}
            className="px-2.5 h-8 rounded-lg text-xs font-semibold border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-600 transition">
            {name}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {PERM_DEFS.map(([k, label]) => {
          const on = set.has(k);
          return (
            <button key={k} type="button" data-testid={`perm-${k}`} aria-pressed={on} onClick={() => toggle(k)}
              className={`h-10 rounded-lg text-sm font-semibold border transition ${on ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-500 hover:border-indigo-300"}`}>
              {label}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-xs text-slate-400">Modules not selected are hidden from this admin.</p>
    </div>
  );
}

function QuotaField({ value, onChange, used }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Employee Quota</label>
      <input
        data-testid="admin-quota"
        type="number"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Leave blank for unlimited"
        className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
      />
      <p className="mt-1 text-xs text-slate-400">
        Max employees this admin can create.{used != null ? ` Currently using ${used}.` : ""} Blank = unlimited.
      </p>
    </div>
  );
}

function permSummary(u) {
  if (u.role === "super_admin") return "All modules";
  const perms = u.permissions || [];
  if (!perms.length) return "No modules";
  return PERM_DEFS.filter(([k]) => perms.includes(k)).map(([, l]) => l).join(", ");
}

function ActionToggles({ canEdit, canDelete, onChange }) {
  const Toggle = ({ on, onClick, label, hint, testid }) => (
    <button type="button" data-testid={testid} aria-pressed={on} onClick={onClick}
      className={`flex items-center justify-between gap-3 w-full h-12 px-3 rounded-lg border transition ${on ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`}>
      <span className="text-left">
        <span className="block text-sm font-semibold text-slate-700">{label}</span>
        <span className="block text-[11px] text-slate-400">{hint}</span>
      </span>
      <span className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition ${on ? "bg-emerald-500" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${on ? "left-4" : "left-0.5"}`} />
      </span>
    </button>
  );
  return (
    <div data-testid="action-toggles">
      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Action Permissions</label>
      <div className="grid grid-cols-1 gap-2">
        <Toggle on={canEdit} onClick={() => onChange({ can_edit: !canEdit, can_delete: canDelete })}
          label="Can edit records" hint="Modify employees & sites in permitted modules" testid="toggle-can-edit" />
        <Toggle on={canDelete} onClick={() => onChange({ can_edit: canEdit, can_delete: !canDelete })}
          label="Can delete records" hint="Remove employees & sites in permitted modules" testid="toggle-can-delete" />
      </div>
      <p className="mt-1.5 text-xs text-slate-400">When off, this admin can only view/create, not edit or delete.</p>
    </div>
  );
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 my-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-head text-lg font-bold text-slate-900">{title}</h3>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-5">{children}</div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function UserManagement() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [bizFilter, setBizFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwdUser, setPwdUser] = useState(null);
  const [pinUser, setPinUser] = useState(null);

  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/admin/users") });
  const { data: businesses = [] } = useQuery({ queryKey: ["businesses"], queryFn: () => api.get("/admin/businesses") });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["users"] }); qc.invalidateQueries({ queryKey: ["quota"] }); };

  const roleMut = useMutation({
    mutationFn: ({ id, role }) => api.patch(`/admin/users/${id}/role`, { role }),
    onSuccess: () => { toast.success("Role updated"); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/users/${id}`),
    onSuccess: () => { toast.success("User removed"); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter((u) => {
      if (filter === "admins" && u.role === "staff") return false;
      if (filter === "staff" && u.role !== "staff") return false;
      if (bizFilter !== "all") {
        if (bizFilter === "none" ? !!u.business_id : u.business_id !== bizFilter) return false;
      }
      const hay = `${u.name || ""} ${u.email || ""} ${u.staff_id || ""} ${u.business_name || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, search, filter, bizFilter]);

  const counts = useMemo(() => ({
    total: users.length,
    admins: users.filter((u) => u.role !== "staff").length,
    staff: users.filter((u) => u.role === "staff").length,
  }), [users]);

  return (
    <div className="space-y-5 animate-fade-up" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-slate-500">{counts.total} users · {counts.admins} admin(s) · {counts.staff} staff</p>
        <button onClick={() => setAddOpen(true)} data-testid="add-admin-btn"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition shadow-sm shadow-indigo-600/20">
          <UserPlus className="h-4 w-4" /> Add Admin
        </button>
      </div>

      <div className="flex items-start gap-2 rounded-xl bg-sky-50 border border-sky-200 px-4 py-2.5 text-xs text-sky-800">
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <p>Admins can manage Sites and Attendance/Reviews. Staff use the mobile app only. To add staff with duty hours &amp; photos, use the <b>Employees</b> page. Reset a staff member's PIN or an admin's password from here.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input data-testid="user-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users…"
              className="w-full h-10 rounded-lg border border-slate-200 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
            {[["all", "All"], ["admins", "Admins"], ["staff", "Staff"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`user-filter-${v}`}
                className={`h-8 px-3 rounded-md text-xs font-semibold transition ${filter === v ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
            ))}
          </div>
          <select
            data-testid="user-business-filter"
            value={bizFilter}
            onChange={(e) => setBizFilter(e.target.value)}
            className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 sm:w-52"
          >
            <option value="all">All Businesses</option>
            {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            <option value="none">— No business —</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase text-slate-400 border-b border-slate-100 bg-slate-50/50">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Identifier</th>
                <th className="px-4 py-3">Business Name</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50" data-testid="users-table-body">
              {isLoading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No users found</td></tr>
              ) : (
                filtered.map((u) => {
                  const isSelf = u.id === me?.id;
                  const isStaff = u.role === "staff";
                  return (
                    <tr key={u.id} className="hover:bg-slate-50/60 transition" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="grid place-items-center h-9 w-9 rounded-full bg-indigo-50 text-indigo-600 text-xs font-bold">
                            {(u.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-800">{u.name} {isSelf && <span className="text-xs text-slate-400">(you)</span>}</span>
                        </div>
                        {!isStaff && <p className="text-[11px] text-slate-400 mt-1 ml-12">{u.title || "Admin"} · {permSummary(u)}{u.role === "admin" ? ` · ${u.can_edit ? "Edit" : "No edit"} · ${u.can_delete ? "Delete" : "No delete"} · Quota: ${u.employees_used ?? 0}/${u.employee_quota == null ? "∞" : u.employee_quota}` : ""}</p>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{isStaff ? (u.staff_id || u.email || "—") : (u.email || u.staff_id || "—")}</td>
                      <td className="px-4 py-3" data-testid={`user-business-${u.id}`}>
                        {u.business_name
                          ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700"><Building2 className="h-3.5 w-3.5 text-indigo-500" />{u.business_name}</span>
                          : <span className="text-xs text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {isStaff ? (
                          <RoleBadge role="staff" />
                        ) : (
                          <select
                            data-testid={`user-role-select-${u.id}`}
                            value={u.role}
                            disabled={isSelf || roleMut.isPending}
                            onChange={(e) => roleMut.mutate({ id: u.id, role: e.target.value })}
                            className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold outline-none focus:border-indigo-500 disabled:opacity-60"
                          >
                            <option value="admin">Admin</option>
                            <option value="super_admin">Super Admin</option>
                          </select>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {!isStaff && (
                            <button onClick={() => setEditUser(u)} data-testid={`edit-user-${u.id}`} title="Edit"
                              className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-indigo-600 transition">
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          {isStaff ? (
                            <button onClick={() => setPinUser(u)} data-testid={`reset-pin-${u.id}`} title="Reset PIN"
                              className="h-8 px-2.5 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-indigo-600 transition text-xs font-semibold inline-flex gap-1">
                              <KeyRound className="h-4 w-4" /> PIN
                            </button>
                          ) : (
                            <button onClick={() => setPwdUser(u)} data-testid={`reset-password-${u.id}`} title="Reset Password"
                              className="h-8 px-2.5 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-indigo-600 transition text-xs font-semibold inline-flex gap-1">
                              <KeyRound className="h-4 w-4" /> Password
                            </button>
                          )}
                          <button
                            onClick={() => { if (!isSelf && window.confirm(`Remove ${u.name}?`)) delMut.mutate(u.id); }}
                            disabled={isSelf}
                            data-testid={`delete-user-${u.id}`}
                            className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition disabled:opacity-30 disabled:hover:bg-transparent"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AddAdminModal open={addOpen} onClose={() => setAddOpen(false)} onDone={invalidate} businesses={businesses} />
      <EditAdminModal user={editUser} onClose={() => setEditUser(null)} onDone={invalidate} businesses={businesses} />
      <ResetPasswordModal user={pwdUser} onClose={() => setPwdUser(null)} />
      <ResetPinModal user={pinUser} onClose={() => setPinUser(null)} />
    </div>
  );
}

function AddAdminModal({ open, onClose, onDone, businesses = [] }) {
  const initial = { name: "", email: "", password: "", role: "admin", title: "Admin", business_id: "", permissions: ["sites", "attendance", "reviews"], can_edit: false, can_delete: false, employee_quota: "", features: { ...DEFAULT_FEATURES } };
  const [form, setForm] = useState(initial);
  const [tab, setTab] = useState("details");
  const [err, setErr] = useState("");
  const mut = useMutation({
    mutationFn: (body) => api.post("/admin/users/admin", body),
    onSuccess: () => { toast.success("Admin created"); onDone(); onClose(); setForm(initial); setTab("details"); },
    onError: (e) => setErr(apiError(e)),
  });
  const submit = (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    const quota = form.employee_quota === "" ? null : Number(form.employee_quota);
    mut.mutate({ ...form, email: form.email.trim().toLowerCase(), employee_quota: quota, business_id: form.business_id || null });
  };
  return (
    <Modal open={open} onClose={onClose} title="Add Admin">
      <form onSubmit={submit} className="space-y-4" data-testid="add-admin-form">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          <button type="button" data-testid="admin-tab-details" onClick={() => setTab("details")} className={tabBtn(tab === "details")}>Details</button>
          <button type="button" data-testid="admin-tab-features" onClick={() => setTab("features")} className={tabBtn(tab === "features")}>Feature Permissions</button>
        </div>

        {tab === "details" && (
          <div className="space-y-4">
            <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-name" required />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="admin-email" required />
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Password</label>
              <PasswordInput className="h-11" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="admin-password" autoComplete="new-password" placeholder="At least 8 characters" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Job Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="admin-title" placeholder="e.g. HR Manager" />
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Role</label>
                <select data-testid="admin-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500">
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Business Name</label>
              <select data-testid="admin-business" value={form.business_id} onChange={(e) => setForm({ ...form, business_id: e.target.value })}
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500">
                <option value="">No business</option>
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
        )}

        {tab === "features" && (
          form.role === "admin" ? (
            <div className="space-y-4">
              <PermissionPicker
                value={form.permissions}
                onChange={(p) => setForm({ ...form, permissions: p })}
                onPreset={(name, perms) => setForm({ ...form, permissions: perms, title: name })}
              />
              <ActionToggles canEdit={form.can_edit} canDelete={form.can_delete} onChange={(v) => setForm({ ...form, ...v })} />
              <QuotaField value={form.employee_quota} onChange={(v) => setForm({ ...form, employee_quota: v })} />
              <FeatureToggles value={form.features} onChange={(f) => setForm({ ...form, features: f })} />
            </div>
          ) : (
            <p className="text-xs text-slate-400 py-2">Super Admin always has access to all modules and every feature.</p>
          )
        )}

        {err && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="add-admin-error">{err}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={mut.isPending} data-testid="add-admin-submit" className="h-10 px-5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Create Admin
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditAdminModal({ user, onClose, onDone, businesses = [] }) {
  const [form, setForm] = useState({ name: "", email: "", title: "", business_id: "", permissions: [], can_edit: false, can_delete: false, employee_quota: "", features: { ...DEFAULT_FEATURES } });
  const [tab, setTab] = useState("details");
  const [err, setErr] = useState("");
  const isSuper = user?.role === "super_admin";
  useEffect(() => {
    if (user) { setForm({ name: user.name || "", email: user.email || "", title: user.title || "", business_id: user.business_id || "", permissions: user.permissions || [], can_edit: !!user.can_edit, can_delete: !!user.can_delete, employee_quota: user.employee_quota == null ? "" : String(user.employee_quota), features: normalizeFeatures(user.features) }); setTab("details"); }
  }, [user]);
  const mut = useMutation({
    mutationFn: (body) => api.patch(`/admin/users/${user.id}`, body),
    onSuccess: () => { toast.success("User updated"); onDone(); onClose(); },
    onError: (e) => setErr(apiError(e)),
  });
  const submit = (e) => {
    e.preventDefault();
    setErr("");
    const body = { name: form.name, email: form.email.trim().toLowerCase(), title: form.title, business_id: form.business_id || null };
    if (!isSuper) { body.permissions = form.permissions; body.can_edit = form.can_edit; body.can_delete = form.can_delete; body.employee_quota = form.employee_quota === "" ? null : Number(form.employee_quota); body.features = form.features; }
    mut.mutate(body);
  };
  return (
    <Modal open={!!user} onClose={onClose} title={`Edit — ${user?.name || ""}`}>
      <form onSubmit={submit} className="space-y-4" data-testid="edit-user-form">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          <button type="button" data-testid="edit-tab-details" onClick={() => setTab("details")} className={tabBtn(tab === "details")}>Details</button>
          <button type="button" data-testid="edit-tab-features" onClick={() => setTab("features")} className={tabBtn(tab === "features")}>Feature Permissions</button>
        </div>

        {tab === "details" && (
          <div className="space-y-4">
            <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-name" required />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="edit-email" required />
            <Field label="Job Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="edit-title" placeholder="e.g. HR Manager" />
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Business Name</label>
              <select data-testid="edit-business" value={form.business_id} onChange={(e) => setForm({ ...form, business_id: e.target.value })}
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500">
                <option value="">No business</option>
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
        )}

        {tab === "features" && (
          isSuper ? (
            <p className="text-xs text-slate-400 py-2">Super Admin always has access to all modules and every feature.</p>
          ) : (
            <div className="space-y-4">
              <PermissionPicker
                value={form.permissions}
                onChange={(p) => setForm({ ...form, permissions: p })}
                onPreset={(name, perms) => setForm({ ...form, permissions: perms, title: name })}
              />
              <ActionToggles canEdit={form.can_edit} canDelete={form.can_delete} onChange={(v) => setForm({ ...form, ...v })} />
              <QuotaField value={form.employee_quota} onChange={(v) => setForm({ ...form, employee_quota: v })} used={user?.employees_used} />
              <FeatureToggles value={form.features} onChange={(f) => setForm({ ...form, features: f })} />
            </div>
          )
        )}

        {err && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="edit-user-error">{err}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={mut.isPending} data-testid="edit-user-submit" className="h-10 px-5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save Changes
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post(`/admin/users/${user.id}/reset-password`, { new_password: pwd }),
    onSuccess: () => { toast.success("Password reset"); onClose(); setPwd(""); },
    onError: (e) => setErr(apiError(e)),
  });
  const submit = (e) => { e.preventDefault(); setErr(""); if (pwd.length < 8) { setErr("Password must be at least 8 characters."); return; } mut.mutate(); };
  return (
    <Modal open={!!user} onClose={onClose} title={`Reset Password — ${user?.name || ""}`}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">New Password</label>
          <PasswordInput className="h-11" value={pwd} onChange={setPwd} testid="reset-password-input" autoComplete="new-password" placeholder="At least 8 characters" required />
        </div>
        {err && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="reset-password-error">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={mut.isPending} data-testid="reset-password-submit" className="h-10 px-5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Reset
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPinModal({ user, onClose }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post(`/admin/users/${user.id}/reset-pin`, { new_pin: pin }),
    onSuccess: () => { toast.success("PIN reset"); onClose(); setPin(""); },
    onError: (e) => setErr(apiError(e)),
  });
  const submit = (e) => { e.preventDefault(); setErr(""); if (!/^\d{4}$/.test(pin)) { setErr("PIN must be exactly 4 digits."); return; } mut.mutate(); };
  return (
    <Modal open={!!user} onClose={onClose} title={`Reset PIN — ${user?.name || ""}`}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-pin-form">
        <Field label="New 4-digit PIN" value={pin} onChange={(v) => setPin(v.replace(/\D/g, "").slice(0, 4))} testid="reset-pin-input" placeholder="####" required />
        {err && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="reset-pin-error">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={mut.isPending} data-testid="reset-pin-submit" className="h-10 px-5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Reset
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
        className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
    </div>
  );
}
